import React, { useRef, useState } from "react";
import { Camera, UploadCloud } from "lucide-react";
import Card from "../components/ui/Card";
import PrimaryButton from "../components/ui/PrimaryButton";
import StatusChip from "../components/ui/StatusChip";
import { mockScanSamples } from "../mock/scans";
import { formatDateTime } from "../utils/format";
import { useNavigate } from "react-router-dom";
import { analyzeImage, AnalysisResult } from "../services/api";
import { useAuth } from "../context/AuthContext";
import "./ScanPage.css";

type ScanPageProps = {
  sampleId: string;
  photoUri: string | null;
  onCapture: (photo: string | null, file: File | null) => void;
  photoFile: File | null;
  onAnalysisComplete: (result: AnalysisResult) => void;
};

const ScanPage: React.FC<ScanPageProps> = ({
  sampleId,
  photoUri,
  onCapture,
  photoFile,
  onAnalysisComplete,
}) => {
  // Use sampleId to get the sample, or default
  const sample = mockScanSamples[sampleId] || mockScanSamples.handClear;

  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const { token } = useAuth();

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { currentTarget } = event;
    const file = currentTarget.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      onCapture(url, file);
    }
    currentTarget.value = "";
  };

  const handleDetect = async () => {
    if (!photoFile) {
      return;
    }

    setIsAnalyzing(true);
    try {
      const result = await analyzeImage(photoFile, token || undefined);
      onAnalysisComplete(result);
      navigate("/results");
    } catch (error) {
      console.error("Analysis failed:", error);
      alert(
        `Analysis failed: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    } finally {
      setIsAnalyzing(false);
    }
  };

  const hasPhoto = Boolean(photoUri);
  const canSubmit = Boolean(photoFile);

  const statusChips = hasPhoto
    ? [
        {
          title: "Capture",
          status: "ok" as const,
          description: "Photo ready for analysis",
        },
      ]
    : [];

  const detectVariant = canSubmit ? "secondary" : "disabled";
  const detectDisabled = !canSubmit || isAnalyzing;
  const hintMessage = !hasPhoto ? "Upload a photo to enable detection." : null;

  return (
    <div className="scan">
      <Card className="scan__card" padding="lg" elevated>
        <header className="scan__header">
          <div>
            <h2>Capture lesion photo</h2>
            <p>Use a clear, well-lit photo for more accurate guidance.</p>
          </div>
        </header>

        <div className="scan__content">
          <div className="scan__preview">
            <button
              type="button"
              className="scan__image"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Upload lesion photo"
            >
              {photoUri ? (
                <img src={photoUri} alt={sample.area} />
              ) : (
                <div className="scan__image-placeholder">
                  <Camera size={64} strokeWidth={1.8} />
                  <span>Awaiting lesion photo</span>
                </div>
              )}
            </button>
            {statusChips.length > 0 && (
              <div className="scan__status-grid">
                {statusChips.map((chip) => (
                  <StatusChip key={chip.title} {...chip} />
                ))}
              </div>
            )}
            <div className="scan__actions">
              <PrimaryButton
                variant="capture"
                icon={<Camera size={18} strokeWidth={2.2} />}
                onClick={() => fileInputRef.current?.click()}
                disabled={isAnalyzing}
              >
                {photoUri ? "Retake / upload again" : "Upload lesion photo"}
              </PrimaryButton>
              {photoUri ? (
                <button
                  className="scan__reset"
                  type="button"
                  onClick={() => onCapture(null, null)}
                  disabled={isAnalyzing}
                >
                  Clear photo
                </button>
              ) : null}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleUpload}
                hidden
              />
            </div>
          </div>
          <div className="scan__sidebar">
            <Card padding="md" className="scan__info">
              <h3>Guidance</h3>
              <p className="scan__info-meta">
                Last scan - {formatDateTime(sample.capturedAt)}
              </p>
              <ul>
                <li>Ensure the lesion fills most of the frame.</li>
                <li>Take a steady photo from straight on at arm’s length.</li>
                <li>Avoid flash to reduce glare.</li>
              </ul>
            </Card>
          </div>
        </div>

        <footer className="scan__footer">
          <PrimaryButton
            variant={detectVariant}
            icon={<UploadCloud size={18} strokeWidth={2.2} />}
            onClick={handleDetect}
            disabled={detectDisabled}
            loading={isAnalyzing}
          >
            {isAnalyzing ? "Analyzing..." : "Upload & detect"}
          </PrimaryButton>
          {hintMessage && <span className="scan__hint">{hintMessage}</span>}
        </footer>
      </Card>
    </div>
  );
};

export default ScanPage;
